import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RedirectTypeBadge, StatusBadge } from "@/components/base/Badges";
import { useAppData } from "@/hooks/useDomains";
import { addDomain, batchRetarget, batchSetStatus, importDomains, jumpUrl, removeDomains, toggleStatus, updateDomain } from "@/lib/store";
import type { ImportRow } from "@/lib/store";
import BatchImportModal from "@/pages/domains/components/BatchImportModal";
import BatchTargetModal from "@/pages/domains/components/BatchTargetModal";
import ConfirmDialog from "@/pages/domains/components/ConfirmDialog";
import DomainFormModal from "@/pages/domains/components/DomainFormModal";
import type { DomainItem, DomainStatus } from "@/types";

const PAGE_SIZE = 8;

type FilterKey = "all" | DomainStatus;

function CheckBox({ checked, onClick, label }: { checked: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      aria-pressed={checked}
      aria-label={label}
      onClick={onClick}
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors cursor-pointer ${
        checked ? "border-primary-500 bg-primary-500 text-background-50" : "border-background-400 bg-background-50 text-transparent"
      }`}
    >
      <i className="ri-check-line text-sm" />
    </button>
  );
}

export default function Domains() {
  const { domains, settings } = useAppData();
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [filter, setFilter] = useState<FilterKey>("all");
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [targetOpen, setTargetOpen] = useState(false);
  const [editing, setEditing] = useState<DomainItem | null>(null);
  const [deleting, setDeleting] = useState<DomainItem | null>(null);
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [notice, setNotice] = useState("");

  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2200);
  };

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return domains.filter((item) => {
      const matchKw =
        !kw ||
        item.domain.toLowerCase().includes(kw) ||
        item.targetUrl.toLowerCase().includes(kw) ||
        item.remark.toLowerCase().includes(kw);
      const matchFilter = filter === "all" || item.status === filter;
      return matchKw && matchFilter;
    });
  }, [domains, keyword, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const filteredIds = filtered.map((item) => item.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.includes(id));
  const existingDomains = useMemo(() => domains.map((item) => item.domain), [domains]);

  const setKeyword = (value: string) => {
    setPage(1);
    if (value) setParams({ q: value });
    else setParams({});
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      if (allFilteredSelected) return prev.filter((id) => !filteredIds.includes(id));
      return Array.from(new Set([...prev, ...filteredIds]));
    });
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleSubmit = async (payload: Omit<DomainItem, "id" | "clicks" | "createdAt">, id?: string) => {
    const result = id ? await updateDomain(id, payload) : await addDomain(payload);
    if (!result.ok) return result.message;
    if (!id) setPage(1);
    return null;
  };

  const handleImport = async (rows: ImportRow[]) => {
    const result = await importDomains(rows);
    if (result.added > 0) {
      setPage(1);
      flash(result.message);
    }
    return result;
  };

  const copyLink = async (item: DomainItem) => {
    const url = jumpUrl(item.id);
    try {
      await navigator.clipboard.writeText(url);
      flash("已复制测试链接");
    } catch {
      flash(url);
    }
  };

  const filters: { key: FilterKey; label: string }[] = [
    { key: "all", label: "全部" },
    { key: "active", label: "运行中" },
    { key: "paused", label: "已暂停" },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-center">
        <p className="text-xs leading-relaxed text-foreground-500 xl:max-w-md">
          测试链接会由这台服务直接返回 301 或 302。域名解析到这里之后，访问该域名也会按同样规则跳转。
        </p>
        <div className="inline-flex w-fit gap-1 rounded-full bg-background-100 p-1">
          {filters.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => {
                setFilter(item.key);
                setPage(1);
              }}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                filter === item.key ? "bg-primary-500 font-medium text-background-50" : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-500">
            <i className="ri-search-line text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索域名、目标网址或备注"
              className="w-full bg-transparent py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 sm:w-64"
            />
          </div>
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="flex items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-upload-line" />
            批量导入
          </button>
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            新增域名
          </button>
        </div>
      </div>

      {notice ? (
        <div className="flex items-center gap-2 rounded-md bg-accent-100 px-4 py-2.5 text-sm text-accent-900">
          <i className="ri-checkbox-circle-line" />
          {notice}
        </div>
      ) : null}

      {selected.length > 0 ? (
        <div className="flex flex-col gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground-800">
            已选 <span className="font-medium text-foreground-950">{selected.length}</span> 个域名
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                void batchSetStatus(selected, "active").then((count) => {
                  flash(count ? `已启用 ${count} 个域名` : "所选域名已经在运行");
                });
              }}
              className="rounded-md bg-background-50 px-3 py-2 text-sm text-foreground-800 cursor-pointer"
            >
              启用
            </button>
            <button
              type="button"
              onClick={() => {
                void batchSetStatus(selected, "paused").then((count) => {
                  flash(count ? `已暂停 ${count} 个域名` : "所选域名已经暂停");
                });
              }}
              className="rounded-md bg-background-50 px-3 py-2 text-sm text-foreground-800 cursor-pointer"
            >
              暂停
            </button>
            <button
              type="button"
              onClick={() => setTargetOpen(true)}
              className="rounded-md bg-background-50 px-3 py-2 text-sm text-foreground-800 cursor-pointer"
            >
              改目标
            </button>
            <button
              type="button"
              onClick={() => setBatchDeleteOpen(true)}
              className="rounded-md bg-secondary-600 px-3 py-2 text-sm text-background-50 cursor-pointer"
            >
              删除
            </button>
            <button type="button" onClick={() => setSelected([])} className="rounded-md px-3 py-2 text-sm text-foreground-600 cursor-pointer">
              取消选择
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="hidden grid-cols-12 gap-4 border-b border-background-200 bg-background-100 px-5 py-3 text-xs font-medium text-foreground-600 lg:grid">
          <div className="col-span-3 flex items-center gap-3">
            <CheckBox checked={allFilteredSelected} onClick={toggleAllFiltered} label="全选当前筛选" />
            中转域名
          </div>
          <div className="col-span-3">目标网址</div>
          <div className="col-span-2">跳转方式</div>
          <div className="col-span-1">状态</div>
          <div className="col-span-1 text-right">点击量</div>
          <div className="col-span-2 text-right">操作</div>
        </div>

        {pageItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-300" />
            <p className="text-sm text-foreground-500">没有找到匹配的域名</p>
            <button type="button" onClick={openCreate} className="mt-1 text-sm font-medium text-primary-700 cursor-pointer">
              立即新增一个
            </button>
          </div>
        ) : (
          <div className="flex flex-col">
            {pageItems.map((item) => (
              <div
                key={item.id}
                className="grid grid-cols-1 gap-3 border-b border-background-200 px-5 py-4 last:border-b-0 lg:grid-cols-12 lg:items-center lg:gap-4"
              >
                <div className="lg:col-span-3">
                  <div className="flex items-center gap-3">
                    <CheckBox
                      checked={selected.includes(item.id)}
                      onClick={() => toggleSelect(item.id)}
                      label={`选择 ${item.domain}`}
                    />
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-global-line" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground-950">{item.domain}</p>
                      <p className="truncate text-xs text-foreground-500">{item.remark || "无备注"}</p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <p className="truncate text-sm text-foreground-700" title={item.targetUrl}>
                    {item.targetUrl}
                  </p>
                  <p className="text-xs text-foreground-400">创建于 {item.createdAt}</p>
                </div>

                <div className="lg:col-span-2">
                  <RedirectTypeBadge type={item.redirectType} />
                </div>

                <div className="lg:col-span-1">
                  <StatusBadge status={item.status} />
                </div>

                <div className="lg:col-span-1 lg:text-right">
                  <span className="text-sm font-medium text-foreground-950">{item.clicks.toLocaleString()}</span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 lg:col-span-2 lg:justify-end">
                  <button
                    type="button"
                    onClick={() => toggleStatus(item.id)}
                    title={item.status === "active" ? "暂停" : "启用"}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-700 cursor-pointer"
                  >
                    <i className={item.status === "active" ? "ri-pause-circle-line" : "ri-play-circle-line"} />
                  </button>
                  <a
                    href={jumpUrl(item.id).replace(window.location.origin, "")}
                    target="_blank"
                    rel="noreferrer"
                    title="测试跳转"
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-external-link-line" />
                  </a>
                  <button
                    type="button"
                    onClick={() => copyLink(item)}
                    title="复制测试链接"
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                  >
                    <i className="ri-file-copy-line" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(item);
                      setFormOpen(true);
                    }}
                    title="编辑"
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                  >
                    <i className="ri-edit-line" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(item)}
                    title="删除"
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-secondary-400 hover:text-secondary-700 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
        <p className="text-xs text-foreground-500">
          共 {filtered.length} 条记录 · 第 {currentPage} / {totalPages} 页
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-40 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-s-line" />
            上一页
          </button>
          <button
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
            className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-40 cursor-pointer whitespace-nowrap"
          >
            下一页
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>

      <DomainFormModal
        open={formOpen}
        editing={editing}
        defaults={{
          redirectType: settings.defaultRedirectType,
          status: settings.autoRedirect ? "active" : "paused",
        }}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />
      <BatchImportModal
        open={importOpen}
        defaultType={settings.defaultRedirectType}
        existing={existingDomains}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
      />
      <BatchTargetModal
        open={targetOpen}
        count={selected.length}
        onClose={() => setTargetOpen(false)}
        onSubmit={async (targetUrl) => {
          const result = await batchRetarget(selected, targetUrl);
          if (!result.ok) return result.message;
          flash("已更新目标网址");
          return null;
        }}
      />
      <ConfirmDialog
        open={!!deleting}
        description={`确定要删除 ${deleting?.domain ?? ""} 的跳转规则吗？删除后该域名将不再跳转，此操作不可撤销。`}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          const id = deleting.id;
          void removeDomains([id]);
          setSelected((prev) => prev.filter((item) => item !== id));
        }}
      />
      <ConfirmDialog
        open={batchDeleteOpen}
        title="批量删除"
        description={`确定删除已选的 ${selected.length} 条跳转规则吗？删除后这些域名将不再跳转。`}
        onClose={() => setBatchDeleteOpen(false)}
        onConfirm={() => {
          const ids = selected;
          setSelected([]);
          void removeDomains(ids).then(() => flash("已删除所选域名"));
        }}
      />
    </div>
  );
}
